import { inject, Injectable } from '@angular/core';
import { ParamMap, Router } from '@angular/router';
import { catchError, Observable } from 'rxjs';
import {
  AuthEntity,
  AuthReferrer,
  AuthStateService,
  PayloadABCToken, PayloadModulrToken
} from '../state/auth-state.service';
import { SubscriptionFeatureService } from './subscription-feature.service';
import { AcsMode } from '../state/project.interface';
import { Routing } from '../../../app.routes';
import { jwtDecode } from 'jwt-decode';
import { ProjectStateService } from '../state/project-state.service';
import { AUTH_HTTP_SERVICE } from '../http/http-service.token';
import { IAuthHttpService } from '../http/auth/auth-http-service.interface';
import { getFingerprint } from '../../helpers/fingerprint';

@Injectable({
  providedIn: 'root'
})
export class AuthFeatureService {
  private projectStateService: ProjectStateService =
    inject(ProjectStateService);
  private router: Router = inject(Router);
  private authStateService: AuthStateService = inject(AuthStateService);
  private authHttpService = inject<IAuthHttpService>(AUTH_HTTP_SERVICE);
  private subscriptionFeatureService: SubscriptionFeatureService = inject(
    SubscriptionFeatureService
  );

  initializeAuthentication(params: ParamMap) {
    if (sessionStorage.getItem('haveToAuth') === 'false') {
      this.authStateService.updateInitStatus('DONE');
      return;
    }
    this.authStateService.updateInitStatus('IN_PROGRESS');
    let token = params.get('token');
    let uuid = params.get('uuid');
    let producerCode = params.get('producer_code');
    let mode: AcsMode = params.get('mode') as AcsMode;
    let entity = params.get('entity') as AuthEntity | null;
    let referrer = params.get('referrer') as AuthReferrer | null;
    this.authStateService.updateIsPublic(
      sessionStorage.getItem('public') === 'true'
    );


    if (token) {
      let tokenPayload: PayloadABCToken|PayloadModulrToken = jwtDecode(token);
      if (this.authStateService.isModulrToken(tokenPayload)) {
        uuid = tokenPayload.uuid;
        entity = tokenPayload.entity;
        mode = tokenPayload.origin;
      }
    }

    if (params.get('token_producer')) {
      let tokenPayload: PayloadModulrToken = jwtDecode(params.get('token_producer')!);
    }

    this.authStateService.updateData({
      token,
      uuid,
      producerCode,
      mode,
      entity,
      referrer
    });

    // S'il manque une info d'authentification, on passe en mode public
    if (
      ((!uuid || !entity) && !token) ||
      this.authStateService.isPublic()
    ) {
      this.initializeWithoutAuthentication();
      this.authStateService.updateHaveToAuth(false);
      return;
    }

    if (entity && uuid && referrer && !token) {
      this.initializeAuthWithIdentifiedEntity(
        uuid!,
        referrer!,
        entity!,
        producerCode,
        mode
      );
      this.authStateService.updateHaveToAuth(false);
      return;
    }

    if (token && !uuid) {
      this.initializeAuthWithAbcToken(token);
      this.authStateService.updateHaveToAuth(false);
      return;
    }

    // Si un token est présent dans l'url, on récupère les infos liées (client ou projet)
    if (token) {
      this.initializeAuthWithToken(uuid!, entity!, producerCode!, mode!);
      this.authStateService.updateHaveToAuth(false);
      return;
    }


    throw new Error('Invalid authentication info');
  }

  private initializeAuthWithToken(
    uuid: string,
    entity: AuthEntity,
    producerCode: string,
    mode: string
  ) {
    const obs$: Observable<any> =
      entity === 'clients'
        ? this.subscriptionFeatureService.getClientInfo(
            uuid,
            producerCode,
            mode
          )
        : this.subscriptionFeatureService.getProjectInfo(
            uuid,
            producerCode,
            mode
          );

    obs$.subscribe({
      next: (_) => this.authStateService.updateInitStatus('DONE'),
      error: (err) => {
        console.error('Auth initialization error', err);
        this.authStateService.updateInitStatus('DONE');
      }
    });
  }

  private initializeAuthWithIdentifiedEntity(
    uuid: string,
    referrer: AuthReferrer,
    entity: AuthEntity,
    producerCode: string | null,
    mode: AcsMode | null
  ) {
    this.authHttpService
      .requestCode(uuid, { referrer: referrer, entity: entity })
      .subscribe({
        next: (_) => {
          // this.router.navigate(['request-code']).then();
          this.router
            .navigate([Routing.REPRISEPROJET], {
              queryParams: {
                uuid,
                entity,
                producer_code: producerCode ?? undefined,
                mode: mode ?? undefined,
                referrer
              }
            })
            .then();
          this.authStateService.updateInitStatus('DONE');
        },
        error: (err) => {
          console.error('Auth initialization error', err);
          this.authStateService.updateInitStatus('DONE');
        }
      });
  }

  private initializeWithoutAuthentication() {
    this.authStateService.updateIsPublic(true);
    this.authStateService.updateInitStatus('DONE');
  }

  private initializeAuthWithAbcToken(token: string) {
    const payload: PayloadABCToken = jwtDecode(token);
    this.authHttpService
      .postExchangeToken({ visitor_token: token, fingerprint: getFingerprint() })
      .pipe(
        catchError((err) => {
          this.router.navigate([Routing.UNAUTHORIZED]).then();
          console.error('Auth initialization error', err);
          this.authStateService.updateInitStatus('DONE');
          throw err;
        })
      )
      .subscribe({
        next: (newToken) => {
          this.authStateService.setVisitorParams(
            payload.broker_id,
            token,
            newToken
          );
          this.projectStateService.setIsNewProject(true);
          this.authStateService.updateInitStatus('DONE');
        }
      });
  }
}
